<template>
  <div class="FineCenter__Import__View">
    <h1>ייבוא מרכז לגביית קנסות</h1>
    <div class="FineCenter__Import__View__Steps">
      <section>
        <b-steps v-model="activeStep">

          <!-- Step 0: File selection -->
          <b-step-item label="בחירת קובץ">
            <div class="ChooseFile__Step">
              <b-field label="בחר קובץ לייבוא">
                <b-field class="file is-primary" :class="{ 'has-name': !!file }">
                  <b-upload v-model="file" class="file-label" rounded accept=".xlsx,.xls">
                    <span class="file-cta">
                      <b-icon class="file-icon" icon="upload"></b-icon>
                      <span class="file-label">בחר קובץ...</span>
                    </span>
                    <span class="file-name" v-if="file">
                      {{ file.name }}
                    </span>
                  </b-upload>
                </b-field>
              </b-field>
              <p class="help">
                קובץ אקסל שהורד מהאזור האישי של מרכז לגביית קנסות — ללא הכנה מוקדמת
              </p>
            </div>
          </b-step-item>

          <!-- Step 1: Analyzing -->
          <b-step-item :clickable="false" label="ניתוח קובץ">
            <div class="Progress__Step">
              <b-progress type="is-primary" :value="progress" show-value format="percent"></b-progress>
              <p class="has-text-centered mt-3">מנתח את הקובץ...</p>
            </div>
          </b-step-item>

          <!-- Step 2: Column review -->
          <b-step-item label="מיפוי שדות">
            <div v-if="tableData.length" class="ColumnReview__Step">
              <b-message type="is-info" size="is-small" class="mb-3">
                העמודות זוהו אוטומטית. ניתן לשנות את המיפוי לפני שממשיכים.
              </b-message>
              <div class="table-scrollable">
                <table-selectable
                  ref="table"
                  :show-row-number="true"
                  :starting-row="1"
                  :headers="columnHeaders"
                  :data="tableData"
                />
              </div>
            </div>
          </b-step-item>

          <!-- Step 3: Confirm -->
          <b-step-item label="אישור ייבוא">
            <div class="Confirm__Step" v-if="analyzeResult">

              <!-- Detected company info -->
              <div class="box mb-5">
                <h3 class="subtitle mb-2">פרטי הקובץ</h3>
                <p><strong>ח.פ שנמצא בקובץ:</strong> {{ analyzeResult.DetectedIdNumber || '—' }}</p>
                <p><strong>מספר דוחות לייבוא:</strong> {{ analyzeResult.RowCount }}</p>
                <p><strong>שם קובץ:</strong> {{ analyzeResult.FileName }}</p>
              </div>

              <!-- Profile selection -->
              <div class="mb-5">
                <h3 class="subtitle mb-2">פרופיל</h3>
                <div v-if="selectedProfile" class="detected-profile mb-3">
                  <b-tag type="is-success" size="is-medium">
                    {{ selectedProfile.Name }}
                    <span class="ml-1">({{ selectedProfile.IdNumber }})</span>
                  </b-tag>
                  <b-button size="is-small" class="mr-2" @click="showProfileSearch = !showProfileSearch">
                    {{ showProfileSearch ? 'סגור' : 'שנה פרופיל' }}
                  </b-button>
                </div>
                <b-message v-else type="is-warning" size="is-small">
                  לא נמצא פרופיל תואם לח.פ {{ analyzeResult.DetectedIdNumber }}. אנא בחר פרופיל ידנית.
                </b-message>

                <div v-if="showProfileSearch || !selectedProfile">
                  <div class="is-flex mb-2">
                    <b-field label="חפש פרופיל" class="ml-3">
                      <b-input v-model="profileTerm" placeholder="שם / ח.פ"></b-input>
                    </b-field>
                    <b-field label="מנוהלים בלבד">
                      <b-switch v-model="isManaged" class="ltr pt-2">
                        {{ isManaged ? 'כן' : 'לא' }}
                      </b-switch>
                    </b-field>
                  </div>
                  <b-table
                    :data="filteredProfiles"
                    :selected.sync="selectedProfile"
                    :striped="true"
                    :hoverable="true"
                    :loading="isLoadingProfiles"
                    @click="onProfileClick"
                    paginated
                    per-page="10"
                    :pagination-simple="true"
                    default-sort="Id"
                    :sticky-header="true"
                  >
                    <b-table-column field="Id" label="מזהה" v-slot="props" centered sortable>
                      {{ props.row.Id }}
                    </b-table-column>
                    <b-table-column label="ח.פ/ת.ז" v-slot="props" centered>
                      {{ props.row.IdNumber }}
                    </b-table-column>
                    <b-table-column field="Name" label="שם" v-slot="props" sortable>
                      {{ props.row.Name }}
                    </b-table-column>
                    <b-table-column label="כתובת" v-slot="props">
                      {{ getAddress(props.row) }}
                    </b-table-column>
                  </b-table>
                </div>
              </div>

              <!-- Import settings -->
              <div class="settings-section">
                <h3 class="subtitle mb-2">הגדרות</h3>
                <b-field label="הערה">
                  <b-input v-model="comment"></b-input>
                </b-field>
                <b-field label="תאריך קובע">
                  <b-datepicker
                    v-model="date"
                    placeholder="לחץ כדי לבחור"
                    icon-prev="chevron-right"
                    icon-next="chevron-left"
                    icon="calendar-today"
                    locale="en-GB"
                    :day-names="['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש']"
                    :month-names="['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר']"
                    close-on-click
                    editable
                    append-to-body
                  ></b-datepicker>
                </b-field>
                <b-field label="ייבוא ראשוני">
                  <b-switch v-model="isFirstTime">
                    {{ isFirstTime ? 'כן' : 'לא' }}
                  </b-switch>
                </b-field>
              </div>
            </div>
          </b-step-item>

          <!-- Step 4: Importing -->
          <b-step-item :clickable="false" :label="'מייבא נתונים'" :visible="isImporting">
            <div class="importing-step">
              <b-loading :is-full-page="false" v-model="isImporting" :can-cancel="false"></b-loading>
            </div>
          </b-step-item>

          <!-- Step 5: Status/Results -->
          <b-step-item class="table-item" label="סטטוס">
            <div class="status-step">
              <div class="results-reports">
                <div v-if="isLoading" style="display:flex;align-items:center;gap:10px">
                  <span>מעבד...</span>
                  <b-icon pack="fas" icon="sync-alt" custom-class="fa-spin"></b-icon>
                </div>
                <p v-if="succeeded">דוחות שהתקבלו: {{ succeeded }}</p>
                <p v-if="notSucceeded">דוחות שלא התקבלו: {{ notSucceeded }}</p>
              </div>
              <div class="is-flex is-justify-content-flex-end">
                <b-tooltip label="ייצוא לאקסל">
                  <b-button @click="exportToExcel">
                    <b-icon icon="file-excel-outline"></b-icon>
                  </b-button>
                </b-tooltip>
              </div>
              <table v-if="results.rows.length" class="table">
                <thead>
                  <tr>
                    <th v-for="(header, i) in results.headers" :key="i">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, ri) in results.rows"
                    :key="ri"
                    :class="{ 'has-error': !row.Succeeded }"
                  >
                    <td v-for="(value, vi) in row.data" :key="vi">{{ value }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-step-item>

          <template #navigation="{ previous, next }">
            <div class="Navigation" v-show="activeStep !== 5">
              <b-button outlined type="is-primary" @click.prevent="onPrevious(previous)">חזור</b-button>
              <b-button outlined type="is-primary" @click.prevent="onNext(next)">המשך</b-button>
            </div>
          </template>
          <template v-if="activeStep === 5">
            <div class="mt-5">
              <b-button type="is-primary" @click="reset">התחל ייבוא חדש</b-button>
            </div>
          </template>
        </b-steps>
      </section>
    </div>
  </div>
</template>

<script>
import FilesService from '@/services/FilesService';
import ReportService from '@/services/ReportService';
import ProfileService from '@/services/ProfileService';
import TableSelectable from '@/components/TableSelectable.vue';
import { saveAs } from 'file-saver';
import moment from 'moment';

export default {
  name: 'AdminImportFineCenter',
  components: { TableSelectable },

  data() {
    return {
      activeStep: 0,
      file: null,
      progress: 0,
      progressRef: null,

      // Analyze result
      analyzeResult: null,

      // Column review (step 2)
      headerFields: [], // field definitions from /api/Reports/Fields
      tableData: [], // [columns row, ...data rows] fed into TableSelectable

      // Profile selection
      allProfiles: [],
      selectedProfile: null,
      profileTerm: null,
      isManaged: true,
      isLoadingProfiles: false,
      showProfileSearch: false,

      // Import settings
      comment: null,
      date: null,
      isFirstTime: false,

      // Import state
      isImporting: false,
      isLoading: false,
      succeeded: 0,
      notSucceeded: 0,
      results: { headers: [], rows: [] },
    };
  },

  computed: {
    // Build the headers array for TableSelectable: one entry per column,
    // each entry is the same shared headerFields array (matching AdminImportReports pattern).
    columnHeaders() {
      if (!this.analyzeResult || !this.headerFields.length) return [];
      return Array.from({ length: this.analyzeResult.Columns.length }, () => this.headerFields);
    },

    filteredProfiles() {
      if (!this.profileTerm) return this.allProfiles;
      const term = this.profileTerm.toLowerCase();
      return this.allProfiles.filter((p) => {
        const values = Object.values(p);
        return values.some((v) => {
          if (v && typeof v !== 'boolean' && typeof v !== 'number') {
            return v.toLowerCase().indexOf(term) > -1;
          }
          return v === parseInt(term, 10);
        });
      });
    },
  },

  async created() {
    await this.loadProfiles();
  },

  methods: {
    async loadProfiles() {
      this.isLoadingProfiles = true;
      const { data } = await ProfileService.all(this.isManaged);
      this.allProfiles = data;
      this.isLoadingProfiles = false;
    },

    onProfileClick(row) {
      this.selectedProfile = row;
      this.showProfileSearch = false;
    },

    getAddress(row) {
      if (!row.City && !row.Street && !row.HouseNumber) return '';
      return `${row.City}, ${row.Street} ${row.HouseNumber}`;
    },

    toDateFormat(value) {
      if (!value) return value;
      if (typeof value === 'object') return moment(value).format('YYYY-MM-DD');
      if (value.indexOf('T') > -1 || value.substr(4, 1) === '-') {
        return moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD');
      }
      return moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
    },

    async onNext() {
      if (this.activeStep === 0) {
        await this.runAnalyze();
      } else if (this.activeStep === 2) {
        this.advanceFromColumnReview();
      } else if (this.activeStep === 3) {
        await this.runImport();
      }
    },

    onPrevious() {
      // Skip the loading steps when going back
      if (this.activeStep === 2) {
        this.activeStep = 0;
      } else if (this.activeStep > 0) {
        this.activeStep -= 1;
      }
    },

    // Step 0 → 1 (loading) → 2 (column review)
    async runAnalyze() {
      if (!this.file) {
        this.$buefy.dialog.alert({
          title: 'שימו לב!',
          message: 'יש לבחור קובץ לפני שממשיכים',
          type: 'is-warning',
          confirmText: 'אוקיי',
        });
        return;
      }

      this.activeStep = 1;
      this.startProgress();

      const formData = new FormData();
      formData.append('file', this.file);

      try {
        const [analyzeRes, fieldsRes] = await Promise.all([
          FilesService.analyzeFineCenter(formData),
          ReportService.getFields(),
        ]);

        this.analyzeResult = analyzeRes.data;
        this.headerFields = fieldsRes.data;

        // Build tableData: prepend column names as row 0 so TableSelectable can use startingRow=1
        this.tableData = [
          this.analyzeResult.Columns,
          ...this.analyzeResult.ProcessedRows,
        ];

        // Auto-select profile if detected
        if (this.analyzeResult.DetectedProfileId) {
          const matched = this.allProfiles.find((p) => p.Id === this.analyzeResult.DetectedProfileId);
          if (matched) this.selectedProfile = matched;
        }
      } catch {
        this.$buefy.dialog.alert({
          title: 'שגיאה',
          message: 'אירעה שגיאה בניתוח הקובץ. ודא שהקובץ הוא קובץ תקני של מרכז לגביית קנסות.',
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle',
          iconPack: 'fa',
          confirmText: 'אוקיי',
        });
        this.activeStep = 0;
        return;
      } finally {
        this.stopProgress();
      }

      this.activeStep = 2;
      // After Vue renders the TableSelectable, auto-map the columns
      this.$nextTick(() => this.autoMapColumns());
    },

    // Auto-map columns in TableSelectable by exact field Name match.
    // The strategy already named each output column with its standard field name
    // (ReportNumber, PlateNumber, etc.) so we get exact matches.
    autoMapColumns() {
      const { table } = this.$refs;
      if (!table || !table.headersModel.length) return;

      const fields = table.headersModel[0]; // shared field-options array
      this.analyzeResult.Columns.forEach((colName, colIndex) => {
        const field = fields.find((f) => f.Name === colName);
        if (!field) return;

        // Update the internal model (tracks which field maps to which column)
        table.onSelect(fields, colIndex, field);

        // Also update the Buefy select's visual state so the dropdown shows the selection
        const selectArr = table.$refs[`select_${colIndex}`];
        if (selectArr && selectArr[0]) {
          selectArr[0].selected = field;
        }
      });
    },

    // Step 2 → 3: validate column mapping before advancing
    advanceFromColumnReview() {
      const { table } = this.$refs;
      if (!table || !table.isValid()) {
        this.$buefy.dialog.alert({
          title: 'שימו לב!',
          message: 'יש להשלים את כל השדות הדרושים לפני המשך',
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle',
          iconPack: 'fa',
          confirmText: 'אוקיי',
        });
        return;
      }
      this.activeStep = 3;
    },

    // Step 3 → 4 (importing) → 5 (results)
    async runImport() {
      if (!this.selectedProfile) {
        this.$buefy.dialog.alert({
          title: 'שימו לב!',
          message: 'יש לבחור פרופיל לייבוא',
          type: 'is-warning',
          confirmText: 'אוקיי',
        });
        return;
      }

      // Use the column mapping the user confirmed in the review step
      const exportedData = this.$refs.table.export();

      this.isImporting = true;
      this.activeStep = 4;

      const payload = {
        Rows: exportedData.rows,
        OriginalRows: exportedData.originalRows,
        Columns: exportedData.columns,
        ProfileId: this.selectedProfile.Id,
        FileName: this.analyzeResult.FileName,
        Comment: this.comment,
        VerificationDate: this.toDateFormat(this.date),
        IsSetup: this.isFirstTime,
        GetScreenshots: false,
      };

      try {
        const { data: batchId } = await ReportService.importReports(payload);
        this.isLoading = true;

        const importTimer = setInterval(() => {
          ReportService.importReportsStatus(batchId).then((r) => {
            this.results = this.buildResults(exportedData.columns, r.data);
            this.succeeded = r.data.Rows.filter((row) => row.Succeeded).length;
            this.notSucceeded = r.data.Rows.filter((row) => !row.Succeeded).length;
            this.isLoading = false;
            this.isImporting = false;
            clearInterval(importTimer);
            this.activeStep = 5;
            this.$buefy.dialog.alert({
              title: 'הפעולה הסתיימה',
              message: `דוחות שהתקבלו: ${this.succeeded}<br>דוחות שלא התקבלו: ${this.notSucceeded}`,
              type: 'is-success',
              html: true,
              icon: 'times-circle',
              iconPack: 'fa',
              confirmText: 'אוקיי',
            });
          });
        }, 2000);
      } catch {
        this.isImporting = false;
        this.activeStep = 3;
        this.$buefy.dialog.alert({
          title: 'שגיאה',
          message: 'אירעה שגיאה בתהליך הייבוא',
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle',
          iconPack: 'fa',
          confirmText: 'אוקיי',
        });
      }
    },

    buildResults(columns, response) {
      const labelMap = {
        ReportNumber: 'מספר דוח',
        MuniReferenceNumber: 'אסמכתא',
        Municipality: 'רשות',
        OriginalAmount: 'סכום מקורי',
        CurrentAmount: 'יתרה',
        PartialPaidAmount: 'שולם',
        DateTaken: 'תאריך עבירה',
        Reason: 'סיבה',
        PlateNumber: 'רכב',
        LastPayDate: 'תאריך אחרון',
        Address: 'כתובת',
        IsPaid: 'שולם?',
      };
      const headers = [...columns.map((c) => labelMap[c] || c), 'שגיאות'];
      const rows = (response.Rows || []).map((r) => ({
        Succeeded: r.Succeeded,
        data: [...(r.Data || []), (r.Errors || []).join('; ')],
      }));
      return { headers, rows };
    },

    async exportToExcel() {
      let data = [this.results.headers];
      data = data.concat(this.results.rows.map((r) => r.data));
      const { data: blob } = await FilesService.postData(data);
      saveAs(new Blob([blob], { type: 'application/vnd.ms-excel' }), 'ייבוא מרכז קנסות.xlsx');
    },

    startProgress() {
      this.progress = 0;
      this.progressRef = setInterval(() => {
        this.progress = Math.min(95, this.progress + 5);
      }, 200);
    },

    stopProgress() {
      this.progress = 100;
      clearInterval(this.progressRef);
    },

    reset() {
      this.activeStep = 0;
      this.file = null;
      this.analyzeResult = null;
      this.tableData = [];
      this.headerFields = [];
      this.selectedProfile = null;
      this.profileTerm = null;
      this.comment = null;
      this.date = null;
      this.isFirstTime = false;
      this.progress = 0;
      this.isImporting = false;
      this.isLoading = false;
      this.succeeded = 0;
      this.notSucceeded = 0;
      this.results = { headers: [], rows: [] };
      this.showProfileSearch = false;
    },
  },

  watch: {
    async isManaged() {
      await this.loadProfiles();
    },
  },
};
</script>

<style scoped lang="scss">
.FineCenter__Import__View h1 {
  font-size: 25px;
  padding: 25px 25px 0 0;
}

.FineCenter__Import__View__Steps {
  padding: 50px 20px 20px 20px;
  display: flex;
  align-content: center;

  ::v-deep .step-content {
    padding: 60px 0 10px 0;
  }

  section {
    width: 100%;
  }

  .ChooseFile__Step {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px 0;
    width: 600px;
    margin: 0 auto;

    .help {
      margin-top: 12px;
      color: #888;
    }

    ::v-deep .field,
    ::v-deep .file {
      text-align: center;
      justify-content: center;
    }
  }

  .Progress__Step {
    width: 600px;
    padding: 30px 0;
    margin: 0 auto;
  }

  .ColumnReview__Step {
    .table-scrollable {
      width: 100%;
    }
  }

  .Confirm__Step {
    max-width: 800px;

    .detected-profile {
      display: flex;
      align-items: center;
    }

    .settings-section {
      max-width: 500px;
    }
  }

  .importing-step {
    height: 300px;
  }

  ::v-deep {
    .b-steps .steps .step-items .step-item:not(:first-child)::before,
    .b-steps .steps .step-items .step-item:only-child::before {
      right: -50%;
      left: unset;
    }

    .b-steps .steps .step-items .step-item::before,
    .b-steps .steps .step-items .step-item::after {
      background-position: left bottom;
      background: linear-gradient(to right, #dbdbdb 50%, #7957d5 50%);
      background-size: 200% 100%;
    }

    .b-steps .steps .step-items .step-item.is-active::before,
    .b-steps .steps .step-items .step-item.is-active::after {
      background-position: right bottom;
    }

    .b-steps .steps .step-items .step-item.is-previous::before,
    .b-steps .steps .step-items .step-item.is-previous::after {
      background-position: right bottom;
    }
  }

  .Navigation {
    display: flex;
    justify-content: center;

    ::v-deep button {
      margin: 0 5px;
    }
  }
}

.table-item {
  height: calc(100vh - 485px);
  overflow-y: auto;
}

.status-step {
  .table {
    width: 100%;
    margin-top: 30px;

    tbody tr {
      background-color: #daffda;
    }
  }

  .has-error {
    background-color: #ffdada !important;
  }
}

::v-deep .table.is-hoverable tbody tr:not(.is-selected):hover {
  background-color: #eaf0f6 !important;
  cursor: pointer;
}

::v-deep .table.is-striped tbody tr:not(.is-selected):nth-child(even) {
  background-color: #efefef;
}

tbody {
  display: block;
  max-height: 300px;
  overflow: auto;
}

thead,
tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

thead {
  width: calc(100% - 1em);
}
</style>
